import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "EN", full: "English" },
  { code: "ja", label: "JA", full: "日本語" },
  { code: "ko", label: "KO", full: "한국어" },
  { code: "zh-TW", label: "繁", full: "繁體中文" },
];

interface LanguageSwitcherProps {
  light?: boolean;
}

export default function LanguageSwitcher({ light = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
          light
            ? "text-white/80 hover:text-white hover:bg-white/10"
            : "text-gray-600 hover:text-emerald-700 hover:bg-emerald-50"
        }`}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line text-sm" />
        </div>
        {current.label}
        <div className="w-3 h-3 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-lg border border-gray-100 py-1 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer text-left ${
                i18n.language === lang.code
                  ? "text-emerald-700 bg-emerald-50 font-semibold"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="font-bold text-xs w-6">{lang.label}</span>
              <span>{lang.full}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
